/**
 * LoginPage
 * หน้าเข้าสู่ระบบ
 *
 * Full-page login screen with GoAudit branding.
 * หน้าจอเข้าสู่ระบบแบบเต็มหน้าพร้อมแบรนด์ GoAudit
 */

import { useState } from 'react'
import { Navigate, useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from './useAuth'
import { LoginForm } from './components/LoginForm'
import type { LoginInput } from './types'

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get redirect path from location state
  const from =
    (location.state as { from?: { pathname: string } | undefined } | undefined)?.from?.pathname ??
    '/'

  // Wait for auth initialization before deciding to redirect
  if (isAuthLoading) {
    return null
  }

  // If already authenticated, redirect (declarative to avoid React warnings)
  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  const onSubmit = async (values: LoginInput) => {
    setError(null)
    setIsLoading(true)
    try {
      const redirectPath = await login(values)
      await navigate(from !== '/' ? from : redirectPath, { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.loginError')
      // Show localized error
      if (message.includes('network') || message.includes('fetch')) {
        setError(t('auth.networkError'))
      } else {
        setError(t('auth.loginError'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#0c2d6b] via-[#1a3f7a] to-[#0f3460] px-4">
      {/* Decorative elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute -right-32 -bottom-32 h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute top-1/4 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-cyan-500/5 blur-2xl" />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-8 shadow-2xl backdrop-blur-xl dark:bg-black/20">
          {/* Logo */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-black tracking-tighter text-white uppercase italic">
              Go<span className="text-[#60a5fa]">Audit</span>
            </h1>
            <p className="mt-1 text-[11px] font-medium tracking-widest text-white/60 uppercase">
              {t('app.description')}
            </p>
          </div>

          {/* Title */}
          <div className="mb-6 text-center">
            <h2 className="text-lg font-bold tracking-wider text-white uppercase">
              {t('auth.signInTitle')}
            </h2>
            <p className="mt-1 text-[11px] font-medium text-white/50">{t('auth.signInSubtitle')}</p>
          </div>

          {/* Login Form */}
          <div className="[&_input]:border-white/20 [&_input]:bg-white/5 [&_input]:text-white [&_input]:placeholder-white/30 [&_label]:text-white/80">
            <LoginForm onSubmit={onSubmit} isLoading={isLoading} error={error} />
          </div>

          {/* Forgot Password Link */}
          <div className="mt-5 text-center">
            <Link
              to="/forgot-password"
              className="text-[11px] font-medium text-white/40 transition-colors hover:text-white/80"
              id="forgot-password-link"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-white/30">
          © {new Date().getFullYear()} GoAudit - {t('app.description')}
        </p>
      </div>
    </div>
  )
}
